var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/** @jsx jsx */
import { useEffect, useState, useCallback, useRef } from "react";
import { MetropolisCities } from "../models/enums/MetropolisCities";
import { FilterSelectFieldEnum } from "../models/enums/FilterSelectFieldEnum";
import Button from "@mui/material/Button";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import FmdGoodOutlinedIcon from "@mui/icons-material/FmdGoodOutlined";
import ListAltOutlinedIcon from "@mui/icons-material/ListAltOutlined";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import InputAdornment from "@mui/material/InputAdornment";
import SearchIcon from "@mui/icons-material/Search";
import debounce from "lodash.debounce";
import { getAllYearsBetweenTwoYears } from "../utils/utils";
import ClearIcon from "@mui/icons-material/Clear";
const LYON_DISTRICTS = [
    "Lyon",
    "Lyon 1er Arrondissement",
    "Lyon 2e Arrondissement",
    "Lyon 3e Arrondissement",
    "Lyon 4e Arrondissement",
    "Lyon 5e Arrondissement",
    "Lyon 6e Arrondissement",
    "Lyon 7e Arrondissement",
    "Lyon 8e Arrondissement",
    "Lyon 9e Arrondissement",
];
export default function SearchEngine(props) {
    // Used to avoid first filters use effect
    const isMounted = useRef(false);
    const [showMoreFilters, setShowMoreFilters] = useState(false);
    const [fieldsOptions, setFieldsOptions] = useState({});
    const projectThematic = decodeURIComponent(document.cookie
        .replace(/(?:(?:^|.*;\s*)projects_thematic\s*\=\s*([^;]*).*$)|^.*$/, "$1")
        .replaceAll("+", " "));
    const [filters, setFilters] = useState(() => {
        if (projectThematic) {
            return {
                ["thematique_du_projet"]: [projectThematic],
            };
        }
        else {
            return {};
        }
    });
    const [keywordInput, setKeywordInput] = useState("");
    const [yearsOptions, setYears] = useState([]);
    const noOptionsText = "Aucune option disponible";
    /**
     * Get all options availables for one project field / meta-key
     * @param field
     * @returns
     */
    function getFieldOption(field) {
        return __awaiter(this, void 0, void 0, function* () {
            const url = process.env.API_URL +
                "/wp-json/enfold-child/v1/option-fields/get/" +
                field;
            const response = yield fetch(url);
            return yield response.json();
        });
    }
    /**
     *
     * @param values
     * @param fieldName
     */
    function handleFilterChange(values, fieldName) {
        return __awaiter(this, void 0, void 0, function* () {
            setFilters((prevState) => (Object.assign(Object.assign({}, prevState), { [fieldName]: values })));
            props.handleClosePopupClickCB();
        });
    }
    /**
     * On component load, get mutliselect options
     */
    useEffect(() => {
        (() => __awaiter(this, void 0, void 0, function* () {
            // ********** retrieve field options ******
            var fieldsOptionResponse = {};
            yield Promise.all(Object.keys(FilterSelectFieldEnum).map((key) => __awaiter(this, void 0, void 0, function* () {
                var options = JSON.parse(yield getFieldOption(FilterSelectFieldEnum[key]));
                if (key == "ObjectifsProjet" || key == "PartenaireDuProjet") {
                    options = createAssociativeArrayForOptionGroup(options);
                }
                fieldsOptionResponse[key] = options;
            })));
            // ************ retrieve projects ****************
            // remove empty filters
            const filtersWithoutEmpty = Object.fromEntries(Object.entries(filters).filter(([key]) => filters[key].length != 0));
            const rawResponse = yield fetch(process.env.API_URL + "/wp-json/enfold-child/v1/projects", {
                method: "POST",
                headers: {
                    Accept: "application/json",
                    "Content-Type": "application/json",
                },
                body: JSON.stringify(filtersWithoutEmpty),
            });
            const content = Object.values(yield rawResponse.json());
            props.mapInterface.filterFeatures(content, false);
            props.handleProjectListChangeCB(content);
            setFieldsOptions(cleanFieldOptionsByProjects(content, fieldsOptionResponse));
        }))();
    }, []);
    /**
     *
     */
    function createAssociativeArrayForOptionGroup(values) {
        var associations = [];
        var group;
        Object.keys(values).forEach((value) => {
            if (value.startsWith("#")) {
                group = value.substring(1);
            }
            else {
                associations.push({ group: group, value: value });
            }
        });
        return associations;
    }
    /**
     * When filters change, get projects and call IMap to refresh webMap
     */
    useEffect(() => {
        (() => __awaiter(this, void 0, void 0, function* () {
            if (isMounted.current) {
                // remove empty filters
                const filtersWithoutEmpty = Object.fromEntries(Object.entries(filters).filter(([key]) => filters[key].length != 0));
                const rawResponse = yield fetch(process.env.API_URL + "/wp-json/enfold-child/v1/projects", {
                    method: "POST",
                    headers: {
                        Accept: "application/json",
                        "Content-Type": "application/json",
                    },
                    body: JSON.stringify(filtersWithoutEmpty),
                });
                const content = Object.values(yield rawResponse.json());
                props.mapInterface.filterFeatures(content, false);
                props.handleProjectListChangeCB(content);
            }
            else {
                isMounted.current = true;
            }
        }))();
    }, [filters]);
    const keywordInputChangedebounce = useCallback(debounce((nextValue) => handleFilterChange(nextValue, "mots_cles"), 1000), [] // will be created only once initially
    );
    const handleKeywordInputChange = (event) => {
        setKeywordInput(event.target.value);
        keywordInputChangedebounce(event.target.value);
    };
    /**
     * Filter field options depending on projects
     * @param projects
     * @param fieldOptionsToFilter
     * @returns
     */
    const cleanFieldOptionsByProjects = (projects, fieldOptionsToFilter) => {
        var filteredOptions = {
            thematique_du_projet: [],
            objectif_du_projet: [],
            partenaire_du_projet: [],
            public_concerne: [],
            annee: [],
            communes: [],
        };
        // ***** THEMATIQUE *****
        filteredOptions["thematique_du_projet"] =
            fieldOptionsToFilter["ThematiquesProjet"];
        // ***** OBJECTIF *****
        const FilteredObjectifsProjet = fieldOptionsToFilter["ObjectifsProjet"].filter((fieldOption) => {
            return projects.find((project) => {
                for (var prop in project.objectif_du_projet) {
                    if (project.objectif_du_projet[prop] == fieldOption.value)
                        return true;
                }
            });
        });
        filteredOptions["objectif_du_projet"] = FilteredObjectifsProjet;
        // ***** PARTENAIRES *****
        const FilteredPartenaire = fieldOptionsToFilter["PartenaireDuProjet"].filter((fieldOption) => {
            return projects.find((project) => {
                for (var prop in project.partenaire_du_projet) {
                    if (project.partenaire_du_projet[prop] == fieldOption.value)
                        return true;
                }
            });
        });
        filteredOptions["partenaire_du_projet"] = FilteredPartenaire;
        // ******** PUBLIC ********
        const FilteredPublic = Object.keys(fieldOptionsToFilter["PublicConcerne"]).filter((fieldOption) => {
            return projects.find((project) => {
                for (var prop in project.public_concerne) {
                    if (project.public_concerne[prop] == fieldOption)
                        return true;
                }
            });
        });
        filteredOptions["public_concerne"] = FilteredPublic;
        // ******** YEARS ********
        var yearsOption = [];
        projects.forEach((project) => {
            yearsOption.push(...getAllYearsBetweenTwoYears(project.date_de_debut.slice(0, 4), project.projet_en_cours == 1
                ? new Date().getFullYear()
                : project.date_de_fin.slice(0, 4)));
        });
        filteredOptions["annee"] = [...new Set(yearsOption.sort())];
        // ***** COMMUNE *****
        const FilteredCommunes = MetropolisCities.filter((city) => {
            return projects.find((project) => {
                return project.commune == city.name;
            });
        });
        // If one project has commune in LYON_DISTRICTS push the option "Lyon"
        const lyonCity = MetropolisCities.find((city) => city.name == "Lyon");
        if (!FilteredCommunes.find((city) => city == lyonCity)) {
            if (projects.find((project) => {
                return LYON_DISTRICTS.includes(project.commune);
            })) {
                FilteredCommunes.push(lyonCity);
            }
        }
        filteredOptions["communes"] = FilteredCommunes;
        return filteredOptions;
    };
    return (_jsxs("div", Object.assign({ id: "search-engine", className: "d-flex flex-column py-4 pt-1-md" }, { children: [_jsxs("div", Object.assign({ className: "d-flex flex-row flex-md-column justify-content-between mb-3 mt-1" }, { children: [_jsx("div", { children: _jsx("span", Object.assign({ className: "title under-line align-self-start" }, { children: "Rechercher, filtrer les projets" })) }), _jsxs("div", Object.assign({ className: "d-flex tabs-lg" }, { children: [_jsxs("div", Object.assign({ className: "se-tab mr-4", onClick: () => props.handleProjectListDisplayCB(false) }, { children: [_jsx(FmdGoodOutlinedIcon, { className: "icon" }), " ", _jsxs("span", Object.assign({ className: ` ${props.showProjectList ? "no-under-line" : "under-line"}` }, { children: [" ", "Carte des projets", " "] }))] })), _jsxs("div", Object.assign({ className: "se-tab mr-4", onClick: () => props.handleProjectListDisplayCB(true) }, { children: [_jsx(ListAltOutlinedIcon, { className: "icon" }), " ", _jsxs("span", Object.assign({ className: ` ${props.showProjectList ? "under-line" : "no-under-line"}` }, { children: [" ", "Liste des projets", " "] }))] }))] }))] })), _jsxs("div", Object.assign({ className: "d-flex flex-row flex-md-column mt-2 mb-1" }, { children: [_jsx(Autocomplete, { className: "mx-2 mb-3 autocomplete", name: "thematique_du_projet", multiple: true, limitTags: 1, disablePortal: true, disabled: projectThematic ? true : false, options: fieldsOptions["thematique_du_projet"]
                            ? Object.keys(fieldsOptions["thematique_du_projet"]).map((option) => {
                                return option;
                            })
                            : [], sx: { width: 300 }, value: filters["thematique_du_projet"] || [], renderInput: (params) => _jsx(TextField, Object.assign({}, params, { label: "Th\u00E9matique" })), renderTags: (value, getTagProps) => value.map((option, index) => (_jsx(Chip, Object.assign({ variant: "outlined", label: option }, getTagProps({ index }))))), onChange: (event, value) => handleFilterChange(value, "thematique_du_projet"), noOptionsText: noOptionsText }), _jsx(Autocomplete, { className: "mx-2 mb-3 autocomplete", multiple: true, limitTags: 1, disablePortal: true, groupBy: (option) => option.group, options: fieldsOptions["objectif_du_projet"]
                            ? fieldsOptions["objectif_du_projet"].map((option) => {
                                return option;
                            })
                            : [], getOptionLabel: (option) => option.value, sx: { width: 300 }, renderInput: (params) => _jsx(TextField, Object.assign({}, params, { label: "Objectif" })), onChange: (event, value) => handleFilterChange(value.map((val) => val.value), "objectif_du_projet"), noOptionsText: noOptionsText }), _jsx(Autocomplete, { className: "mx-2 mb-3 autocomplete", multiple: true, limitTags: 1, disablePortal: true, options: fieldsOptions["annee"] || [], sx: { width: 300 }, renderInput: (params) => _jsx(TextField, Object.assign({}, params, { label: "Ann\u00E9e" })), onChange: (event, value) => handleFilterChange(value, "annee"), noOptionsText: noOptionsText }), _jsx("div", Object.assign({ className: "filter-btns-lg" }, { children: (() => {
                            if (!showMoreFilters) {
                                return (_jsx(Button, Object.assign({ className: "mx-2 moreFilterBtn", variant: "outlined", onClick: () => setShowMoreFilters(true) }, { children: "Plus de filtres" })));
                            }
                            else {
                                return (_jsx(Button, Object.assign({ className: "mx-2 lessFilterBtn", variant: "outlined", onClick: () => setShowMoreFilters(false) }, { children: "Moins de filtres" })));
                            }
                        })() }))] })), showMoreFilters && (_jsxs("div", Object.assign({ className: "d-flex flex-column flex-md-column" }, { children: [_jsxs("div", Object.assign({ className: "d-flex flex-row flex-sm-column mb-1" }, { children: [_jsx(Autocomplete, { className: "mx-2 mb-3 autocomplete", multiple: true, limitTags: 1, disablePortal: true, groupBy: (option) => option.group, options: fieldsOptions["partenaire_du_projet"]
                                    ? fieldsOptions["partenaire_du_projet"].map((option) => {
                                        return option;
                                    })
                                    : [], getOptionLabel: (option) => option.value, sx: { width: 300 }, renderInput: (params) => (_jsx(TextField, Object.assign({}, params, { label: "Partenaire" }))), onChange: (event, value) => handleFilterChange(value.map((val) => val.value), "partenaire_du_projet"), noOptionsText: noOptionsText }), _jsx(Autocomplete, { className: "mx-2 mb-3 autocomplete", multiple: true, limitTags: 1, disablePortal: true, options: fieldsOptions["public_concerne"]
                                    ? fieldsOptions["public_concerne"].map((option) => {
                                        return option;
                                    })
                                    : [], sx: { width: 300 }, renderInput: (params) => (_jsx(TextField, Object.assign({}, params, { label: "Public cible" }))), onChange: (event, value) => handleFilterChange(value, "public_concerne"), noOptionsText: noOptionsText }), _jsx(Autocomplete, { className: "mx-2 mb-3 autocomplete", limitTags: 1, multiple: true, id: "country-select-demo", sx: { width: 300 }, options: fieldsOptions["communes"] || [], autoHighlight: true, getOptionLabel: (option) => option.name + " (" + option.postcode + ")", renderOption: (props, option) => (_jsxs(Box, Object.assign({ component: "li" }, props, { children: [option.name, " (", option.postcode, ")"] }))), renderInput: (params) => (_jsx(TextField, Object.assign({}, params, { label: "Commune" }))), onChange: (event, value) => handleFilterChange(value.flatMap((val) => val.name == "Lyon" ? LYON_DISTRICTS : val.name), "commune"), noOptionsText: noOptionsText })] })), _jsx("div", Object.assign({ className: "keyword-input mb-1" }, { children: _jsx(TextField, { className: "mx-2 textfield", 
                            // id="filled-search"
                            label: "Recherche par mots cl\u00E9s", value: keywordInput, 
                            // type="search"
                            variant: "outlined", onChange: handleKeywordInputChange, InputProps: keywordInput.length != 0
                                ? {
                                    // type: "search",
                                    endAdornment: (_jsx("div", Object.assign({ onClick: () => {
                                            setKeywordInput("");
                                            handleFilterChange("", "mots_cles");
                                        } }, { children: _jsx(InputAdornment, Object.assign({ className: "clear-input-icon", position: "end" }, { children: _jsx(ClearIcon, {}) })) }))),
                                }
                                : {
                                    // type: "search",
                                    endAdornment: (_jsx(InputAdornment, Object.assign({ position: "end" }, { children: _jsx(SearchIcon, {}) }))),
                                } }) }))] }))), _jsx("div", Object.assign({ className: "filter-btns-md" }, { children: (() => {
                    if (!showMoreFilters) {
                        return (_jsx(Button, Object.assign({ className: "mx-2 moreFilterBtn", variant: "outlined", onClick: () => setShowMoreFilters(true) }, { children: "Plus de filtres" })));
                    }
                    else {
                        return (_jsx("div", Object.assign({ className: "less-filter-btn-container" }, { children: _jsx(Button, Object.assign({ className: "mx-2 lessFilterBtn", variant: "outlined", onClick: () => setShowMoreFilters(false) }, { children: "Moins de filtres" })) })));
                    }
                })() })), _jsxs("div", Object.assign({ className: "d-flex tabs-md justify-content-around w-100" }, { children: [_jsxs("div", Object.assign({ className: "se-tab mr-4 d-flex flex-md-raw", onClick: () => props.handleProjectListDisplayCB(false) }, { children: [_jsx(FmdGoodOutlinedIcon, { className: "icon" }), _jsx("div", { children: _jsxs("span", Object.assign({ className: ` ${props.showProjectList
                                        ? "no-under-line deselected"
                                        : "under-line selected"}` }, { children: [" ", "Carte des projets", " "] })) })] })), _jsxs("div", Object.assign({ className: "se-tab d-flex flex-md-raw", onClick: () => props.handleProjectListDisplayCB(true) }, { children: [_jsx(ListAltOutlinedIcon, { className: "icon" }), _jsx("div", { children: _jsxs("span", Object.assign({ className: ` ${props.showProjectList
                                        ? "under-line selected"
                                        : "no-under-line deselected"}` }, { children: [" ", "Liste des projets", " "] })) })] }))] }))] })));
}
